import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Select,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { FiSearch, FiEdit, FiPhone, FiMail } from 'react-icons/fi';
import {
  getAllJobApplications,
  updateJobApplicationStatus,
  JobApplication,
  STATUS_COLORS,
} from '../../services/crmService';

// Cast icons as React.ElementType
const FiSearchIcon = FiSearch as React.ElementType;
const FiEditIcon = FiEdit as React.ElementType;
const FiPhoneIcon = FiPhone as React.ElementType;
const FiMailIcon = FiMail as React.ElementType;

interface Props {
  onDataChange: () => void;
}

const JobApplicationsTab: React.FC<Props> = ({ onDataChange }) => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await getAllJobApplications();
      setApplications(data);
    } catch (error) {
      console.error('Error fetching job applications:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch job applications',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    applicationId: string,
    newStatus: JobApplication['status'],
    notes?: string
  ) => {
    try {
      await updateJobApplicationStatus(applicationId, newStatus, notes);
      toast({
        title: 'Status updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchApplications();
      onDataChange();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Filter applications
  const filteredApplications = applications.filter((application) => {
    const matchesSearch =
      !searchTerm ||
      application.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.position.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || application.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
      {/* Filters */}
      <HStack mb={4} spacing={4}>
        <InputGroup flex="1">
          <InputLeftElement pointerEvents="none">
            <FiSearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <Select
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          w="200px"
        >
          <option value="new">New</option>
          <option value="reviewing">Reviewing</option>
          <option value="interview">Interview</option>
          <option value="hired">Hired</option>
          <option value="declined">Declined</option>
        </Select>
      </HStack>

      {/* Table */}
      {filteredApplications.length > 0 ? (
        <Box overflowX="auto" overflowY="auto" maxH="600px" border="1px solid" borderColor="gray.200" borderRadius="md">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Applicant</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Position</Th>
                <Th>Status</Th>
                <Th>Applied Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredApplications.map((application) => (
                <Tr key={application.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="bold">{application.name}</Td>
                  <Td>
                    <HStack>
                      <Text fontSize="sm">{application.email}</Text>
                      <IconButton
                        aria-label="Email"
                        icon={React.createElement(FiMailIcon)}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        as="a"
                        href={`mailto:${application.email}`}
                      />
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Text fontSize="sm">{application.phone}</Text>
                      <IconButton
                        aria-label="Call"
                        icon={React.createElement(FiPhoneIcon)}
                        size="xs"
                        variant="ghost"
                        colorScheme="green"
                        as="a"
                        href={`tel:${application.phone}`}
                      />
                    </HStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple" fontSize="xs">
                      {application.position}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS.job[application.status]}>
                      {application.status}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{formatDate(application.createdAt)}</Td>
                  <Td>
                    <Tooltip label="Edit">
                      <IconButton
                        aria-label="Edit"
                        icon={React.createElement(FiEditIcon)}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => {
                          setSelectedApplication(application);
                          onOpen();
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Text color="gray.500" textAlign="center" py={8}>
          {searchTerm || statusFilter
            ? 'No applications match your filters'
            : 'No job applications found'}
        </Text>
      )}

      {/* Edit Modal */}
      {selectedApplication && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Job Application Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Applicant:</Text>
                  <Text>{selectedApplication.name}</Text>
                  <Text fontSize="sm" color="gray.600">{selectedApplication.email}</Text>
                  <Text fontSize="sm" color="gray.600">{selectedApplication.phone}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Position:</Text>
                  <Badge colorScheme="purple" fontSize="md">{selectedApplication.position}</Badge>
                </Box>
                {selectedApplication.message && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>About the Applicant:</Text>
                    <Text fontSize="sm" bg="gray.50" p={3} borderRadius="md" whiteSpace="pre-wrap">
                      {selectedApplication.message}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text fontWeight="bold" mb={2}>Application Status:</Text>
                  <Select
                    value={selectedApplication.status}
                    onChange={(e) =>
                      setSelectedApplication({
                        ...selectedApplication,
                        status: e.target.value as JobApplication['status'],
                      })
                    }
                  >
                    <option value="new">New</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="interview">Interview</option>
                    <option value="hired">Hired</option>
                    <option value="declined">Declined</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Internal Notes:</Text>
                  <Textarea
                    value={selectedApplication.notes || ''}
                    onChange={(e) =>
                      setSelectedApplication({
                        ...selectedApplication,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Add notes about interview, feedback, next steps..."
                    rows={4}
                  />
                </Box>
                <Box bg="blue.50" p={3} borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold" mb={1}>Quick Actions:</Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={React.createElement(FiMailIcon)}
                      as="a"
                      href={`mailto:${selectedApplication.email}`}
                    >
                      Email
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="green"
                      leftIcon={React.createElement(FiPhoneIcon)}
                      as="a"
                      href={`tel:${selectedApplication.phone}`}
                    >
                      Call
                    </Button>
                  </HStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() =>
                  handleStatusUpdate(
                    selectedApplication.id,
                    selectedApplication.status,
                    selectedApplication.notes
                  )
                }
              >
                Save Changes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default JobApplicationsTab;
